import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Card,
  Spinner,
  Container,
  Table,
  Badge,
  Tabs,
  Tab,
  Dropdown,
} from "react-bootstrap";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getCSRFToken } from "../utils/csrf";
import {
  Algo,
  MACrossoverParams,
  AssetClass,
  Symbol,
  TradingInstance,
} from "../types";
import AddAlgoModal from "../components/Modals/AddAlgoModal";
import AlgoInstancesModal from "../components/Modals/AlgoInstancesModal";
import { Edit2, Trash2 } from "lucide-react";

const Algos: React.FC = () => {
  const navigate = useNavigate();
  const [algos, setAlgos] = useState<Algo[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAlgo, setSelectedAlgo] = useState<Algo | null>(null);
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [symbols, setSymbols] = useState<Symbol[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedAssetClass, setSelectedAssetClass] = useState<string>("all");
  const [selectedSymbol, setSelectedSymbol] = useState<string>("all");
  const [availableAssetClasses, setAvailableAssetClasses] = useState<string[]>(
    []
  );
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [showInstancesModal, setShowInstancesModal] = useState(false);
  const [selectedAlgoInstances, setSelectedAlgoInstances] = useState<
    TradingInstance[]
  >([]);
  const [algoInstancesMap, setAlgoInstancesMap] = useState<
    Record<number, TradingInstance[]>
  >({});

  // Form states
  const [algoName, setAlgoName] = useState("");
  const [algoDescription, setAlgoDescription] = useState("");
  const [algoParameters, setAlgoParameters] = useState("{}");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAlgoInstances = async (algoId: number) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/algos/${algoId}/instances`
      );
      console.log("Algo instances: ", response.data);
      setSelectedAlgoInstances(response.data);
      setShowInstancesModal(true);
    } catch (error) {
      console.error("Error fetching algo instances:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [algosResponse, assetClassesResponse, symbolsResponse] =
          await Promise.all([
            axios.get(`${BACKEND_URL}/api/algos`),
            axios.get(`${BACKEND_URL}/api/asset-classes`),
            axios.get(`${BACKEND_URL}/api/symbols`),
          ]);

        const algosData = algosResponse.data;
        setAlgos(algosData);
        setAssetClasses(assetClassesResponse.data);
        setSymbols(symbolsResponse.data);

        // Fetch instances for each algo
        const instancesPromises = algosData.map((algo: Algo) =>
          axios
            .get(`${BACKEND_URL}/api/algos/${algo.id}/instances`)
            .then((response) => ({ algoId: algo.id, instances: response.data }))
            .catch((error) => {
              console.error(
                `Error fetching instances for algo ${algo.id}:`,
                error
              );
              return { algoId: algo.id, instances: [] };
            })
        );

        const instancesResults = await Promise.all(instancesPromises);
        const instancesMap = instancesResults.reduce(
          (acc, { algoId, instances }) => {
            acc[algoId] = instances;
            return acc;
          },
          {} as Record<number, TradingInstance[]>
        );

        setAlgoInstancesMap(instancesMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Extract unique asset classes and symbols from algos
  useEffect(() => {
    const assetClasses = new Set<string>();
    const symbols = new Set<string>();

    algos.forEach((algo) => {
      if (algo.parameters?.assetClasses) {
        algo.parameters.assetClasses.forEach((ac: any) => {
          assetClasses.add(ac.type);
        });
      }
      if (algo.parameters?.symbols) {
        algo.parameters.symbols.forEach((symbol: any) => {
          symbols.add(symbol.symbol);
        });
      }
    });

    setAvailableAssetClasses(Array.from(assetClasses).sort());
    setAvailableSymbols(Array.from(symbols).sort());
  }, [algos]);

  const handleAddAlgo = async (algo: Partial<Algo>) => {
    try {
      // Create algo with relationships
      const algoData = {
        name: algo.name,
        description: algo.description,
        parameters: algo.parameters || {},
        asset_classes: algo.asset_classes?.map((ac) => ac.id) || [],
        symbols: algo.symbols?.map((s) => s.id) || [],
        cash_at_risk: algo.cash_at_risk || 0,
        returns: algo.returns || 0,
      };

      const response = await axios.post(`${BACKEND_URL}/api/algos`, algoData);

      // Update local state with returned algo (including relationships)
      setAlgos((prevAlgos) => [...prevAlgos, response.data]);
      setShowAddModal(false);
    } catch (error) {
      console.error("Error adding algo:", error);
    }
  };

  const fetchAlgos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/algos`);
      setAlgos(response.data);
    } catch (error) {
      console.error("Error fetching algos:", error);
    }
  };

  const handleEditAlgo = async (id: number, params: MACrossoverParams) => {
    try {
      await axios.put(`${BACKEND_URL}/api/algos/${id}`, params);
      fetchAlgos();
      setShowAddModal(false);
    } catch (error) {
      console.error("Error editing algo:", error);
    }
  };

  const handleDeleteAlgo = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this algorithm?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/algos/${id}`);
        fetchAlgos();
      } catch (error) {
        console.error("Error deleting algo:", error);
      }
    }
  };

  // Reset editing state when modal is closed
  const handleModalClose = () => {
    setShowAddModal(false);
    setAlgoName("");
    setAlgoDescription("");
  };

  const handleToggleFavorite = async (algo: Algo) => {
    try {
      const csrfToken = await getCSRFToken();
      const response = await axios.put(
        `${BACKEND_URL}/api/algos/${algo.id}`,
        {
          ...algo,
          favorite: !algo.favorite,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      console.log("Response: ", response.data);

      setAlgos((prevAlgos) =>
        prevAlgos.map((a) => (a.id === algo.id ? response.data : a))
      );
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const handleEdit = (algo: Algo) => {
    // Navigate to AlgoStudio with the algo data
    navigate(`/algo-studio/${algo.id}`, {
      state: {
        algo: {
          ...algo,
          parameters: {
            algorithm: algo.parameters.algorithm || {},
            assetClasses: algo.parameters.assetClasses || [],
            symbols: algo.parameters.symbols || [],
            risk: algo.parameters.risk || {},
            schedule: algo.parameters.schedule || {},
          },
        },
      },
    });
  };

  const handleUpdate = async (updatedAlgo: Partial<Algo>) => {
    try {
      if (!selectedAlgo) return;

      const algoData = {
        name: updatedAlgo.name,
        description: updatedAlgo.description,
        parameters: updatedAlgo.parameters || {},
        asset_classes: updatedAlgo.asset_classes?.map((ac) => ac.id) || [],
        symbols: updatedAlgo.symbols?.map((s) => s.id) || [],
        cash_at_risk: updatedAlgo.cash_at_risk || 0,
        returns: updatedAlgo.returns || 0,
      };

      const response = await axios.put(
        `${BACKEND_URL}/api/algos/${selectedAlgo.id}`,
        algoData
      );

      // Update algos list with edited algo
      setAlgos((prevAlgos) =>
        prevAlgos.map((algo) =>
          algo.id === selectedAlgo.id ? response.data : algo
        )
      );

      setShowEditModal(false);
      setSelectedAlgo(null);
    } catch (error) {
      console.error("Error updating algo:", error);
    }
  };

  const filteredAlgos = algos.filter((algo) => {
    const matchesSearch = algo.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesAssetClass =
      selectedAssetClass === "all" ||
      algo.parameters?.assetClasses?.some(
        (ac: any) => ac.type === selectedAssetClass
      );
    const matchesSymbol =
      selectedSymbol === "all" ||
      algo.parameters?.symbols?.some(
        (symbol: any) => symbol.symbol === selectedSymbol
      );

    return (
      matchesSearch &&
      matchesAssetClass &&
      matchesSymbol &&
      (activeTab === "favorites" ? algo.favorite : true)
    );
  });

  const renderAssetClassBadges = (algo: Algo) => {
    const assetClasses = algo.parameters?.assetClasses || [];
    return assetClasses.map((ac: any) => (
      <Badge key={ac.type} bg="primary" className="me-1">
        {ac.type}
      </Badge>
    ));
  };

  const renderSymbolBadges = (algo: Algo) => {
    const symbols = algo.parameters?.symbols || [];
    return symbols.map((symbol: any) => (
      <Badge key={symbol.symbol} bg="secondary" className="me-1">
        {symbol.symbol}
      </Badge>
    ));
  };

  const renderAlgoCard = (algo: Algo) => {
    return (
      <tr key={algo.id}>
        <td style={{ width: "50px" }}>
          <IconButton
            onClick={() => handleToggleFavorite(algo)}
            size="small"
            style={{
              color: algo.favorite ? "#FFD700" : "inherit",
              transition: "color 0.2s ease-in-out",
            }}
          >
            {algo.favorite ? (
              <StarIcon style={{ color: "#FFD700" }} />
            ) : (
              <StarBorderIcon />
            )}
          </IconButton>
        </td>
        <td>{algo.name}</td>
        <td>{algo.description}</td>
        <td>{renderAssetClassBadges(algo)}</td>
        <td>{renderSymbolBadges(algo)}</td>
        <td>
          <Button
            variant="link"
            className="p-0"
            onClick={() => fetchAlgoInstances(algo.id)}
          >
            {algoInstancesMap[algo.id]?.length || 0}
          </Button>
          <span> running</span>
        </td>
        <td>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2"
            onClick={() => handleEdit(algo)}
          >
            <Edit2 size={16} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteAlgo(algo.id)}
          >
            <Trash2 size={16} />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Typography variant="h4">Algos</Typography>
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            type="text"
            placeholder="Search"
            className="me-2"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <IconButton>
            <SearchIcon />
          </IconButton>

          <Dropdown>
            <Dropdown.Toggle
              variant="outline-secondary"
              id="asset-class-dropdown"
            >
              {selectedAssetClass === "all"
                ? "All Asset Classes"
                : selectedAssetClass}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                active={selectedAssetClass === "all"}
                onClick={() => setSelectedAssetClass("all")}
              >
                All Asset Classes
              </Dropdown.Item>
              <Dropdown.Divider />
              {availableAssetClasses.map((ac) => (
                <Dropdown.Item
                  key={ac}
                  active={selectedAssetClass === ac}
                  onClick={() => setSelectedAssetClass(ac)}
                >
                  {ac}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" id="symbol-dropdown">
              {selectedSymbol === "all" ? "All Symbols" : selectedSymbol}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                active={selectedSymbol === "all"}
                onClick={() => setSelectedSymbol("all")}
              >
                All Symbols
              </Dropdown.Item>
              <Dropdown.Divider />
              {availableSymbols.map((symbol) => (
                <Dropdown.Item
                  key={symbol}
                  active={selectedSymbol === symbol}
                  onClick={() => setSelectedSymbol(symbol)}
                >
                  {symbol}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Typography variant="h5">Available Algos</Typography>
          <Button variant="primary" onClick={() => navigate("/algo-studio")}>
            Create Algo
          </Button>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "all")}
          className="mb-3"
        >
          <Tab eventKey="all" title="All Algos">
            <div className="gogi-table-wrap">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Asset Classes</th>
                  <th>Symbols</th>
                  <th>Instances</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                ) : (
                  filteredAlgos.map((algo) => renderAlgoCard(algo))
                )}
              </tbody>
            </Table>
            </div>
          </Tab>
          <Tab eventKey="favorites" title="Favorites">
            <div className="gogi-table-wrap">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Asset Classes</th>
                  <th>Symbols</th>
                  <th>Instances</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                ) : (
                  filteredAlgos.map((algo) => renderAlgoCard(algo))
                )}
              </tbody>
            </Table>
            </div>
          </Tab>
        </Tabs>
      </div>

      <AddAlgoModal
        show={showAddModal}
        onHide={handleModalClose}
        onAdd={handleAddAlgo}
        assetClasses={assetClasses}
        symbols={symbols}
      />

      {selectedAlgo && (
        <AddAlgoModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedAlgo(null);
          }}
          onAdd={handleUpdate}
          assetClasses={assetClasses}
          symbols={symbols}
          initialData={selectedAlgo}
          isEditing={true}
        />
      )}

      <AlgoInstancesModal
        show={showInstancesModal}
        onHide={() => setShowInstancesModal(false)}
        instances={selectedAlgoInstances}
      />
    </Container>
  );
};

export default Algos;
